import React from 'react';
import { ArrowRight, ArrowDown, Smartphone, Monitor, CheckCircle, Code2, Users, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, strengths, images } from '../data/mock';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 pattern-grid" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-20 left-20 w-[400px] h-[400px] bg-gradient-to-tr from-cyan-400/15 to-blue-400/15 rounded-full blur-[80px]" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8 animate-fade-up">
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-blue-700 text-sm font-medium">Trusted Software Consulting Partner</span>
            </div>

            {/* Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.1] mb-6 font-display animate-fade-up">
              {companyInfo.heroTitle}
              <span className="block mt-2 gradient-text">{companyInfo.heroHighlight}</span>
            </h1>

            {/* Description */}
            <p className="text-xl text-slate-600 max-w-xl mb-10 leading-relaxed animate-fade-up" style={{ animationDelay: '0.2s' }}>
              {companyInfo.heroDescription}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-12 animate-fade-up" style={{ animationDelay: '0.4s' }}>
              <Button
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-gradient text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all group"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => document.querySelector('#solutions')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 px-8 py-6 text-lg rounded-xl transition-all"
              >
                View Solutions
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              {strengths.map((stat) => (
                <div key={stat.id} className="text-center">
                  <div className="text-3xl font-bold text-slate-900 font-display">{stat.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Software Consulting Image */}
          <div className="relative hidden lg:block">
            {/* Main Image - Software Professional */}
            <div className="relative z-10 animate-fade-up" style={{ animationDelay: '0.3s' }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/20">
                <img
                  src={images.hero}
                  alt="Software Consulting Professional"
                  className="w-full h-[450px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
              </div>
            </div>

            {/* Floating Code Card */}
            <div className="absolute -left-8 bottom-20 z-20 animate-float">
              <div className="bg-white rounded-2xl p-4 shadow-xl shadow-blue-500/10 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Code2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Custom Software</div>
                    <div className="text-xs text-slate-500">Tailored Solutions</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Badge - Top Right */}
            <div className="absolute -right-5 top-10 bg-white rounded-2xl p-4 shadow-xl shadow-blue-500/10 border border-slate-100 animate-fade-up" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Enterprise Apps</div>
                  <div className="text-xs text-slate-500">Scalable Systems</div>
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="absolute left-10 top-5 bg-white rounded-2xl p-4 shadow-xl shadow-cyan-500/10 border border-slate-100 animate-fade-up" style={{ animationDelay: '0.7s' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Secure</div>
                  <div className="text-xs text-slate-500">By Design</div>
                </div>
              </div>
            </div>

            {/* Team Badge */}
            <div className="absolute -right-3 bottom-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-4 shadow-xl shadow-blue-500/30 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-white" />
                <div>
                  <div className="text-lg font-bold text-white">50+</div>
                  <div className="text-xs text-white/80">Expert Team</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={() => document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-blue-600 transition-colors animate-bounce"
      >
        <ArrowDown size={28} />
      </button>
    </section>
  );
};

export default Hero;
